import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import StandardModal from 'components/shared/StandardModal';
import { ActionContext } from 'hooks/useAdminListPage';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { HighCostMedicationList } from 'models/HighCostMedicationList';
import { adminClientsQueryKeys, adminIndexClients } from 'services/api/admin/clients';
import { createAdminHighCostMedicationList } from 'services/api/admin/highCostMedicationLists';
import { BodySmall } from 'styles/typography';

type ClientOption = { id: string; name: string };

export type AddHighCostMedListModalProps = {
  onClose: () => void;
  ctx: ActionContext;
};

// Admin "Manually Add New List" flow: pick a client with no active high cost medication list, create the
// empty list, then open the existing slideout so the admin can start adding medications. The
// alternative entry point is the CSV upload; this one is for a contract list short enough to type or
// not yet in hand as a file.
//
// The dropdown is searchable + infinite-scroll and only offers inbound-referrals-enabled clients
// without an active list (the `high_cost_med_list_eligible` filter, shared with the upload modal), so
// a successful create never collides with an existing list.
export default function AddHighCostMedListModal({ onClose, ctx }: AddHighCostMedListModalProps) {
  const [client, setClient] = useState<ClientOption | null>(null);
  const invalidateQueries = useInvalidateQueries();

  const clientOptions = useInfiniteOptions<ClientOption>({
    queryKey: adminClientsQueryKeys.highCostMedListEligible,
    fetchFn: (params, signal) => adminIndexClients(params, signal),
    params: { sortBy: 'name asc', highCostMedListEligible: true },
  });

  const { mutate, isPending } = useMutateWithToast({
    mutationFn: createAdminHighCostMedicationList,
  });

  const handleCreate = () => {
    if (!client) return;

    mutate(
      { clientId: client.id },
      {
        onSuccess: (list: HighCostMedicationList) => {
          // The client now has a list, so drop it from the eligible-clients picker the upload modal
          // shares; refresh the tab.
          invalidateQueries({ highCostMedListEligibleClients: true });
          ctx.toast(`High cost medication list created for ${client.name}`);
          ctx.refetch();
          onClose();
          ctx.openSlideout('items', list);
        },
      }
    );
  };

  return (
    <StandardModal title='Add New List' onCancel={onClose}>
      <Description>
        Select a client to start a new high cost medication list. You&rsquo;ll add medications to it next.
      </Description>

      <FieldContainer>
        <FieldLabel>Client</FieldLabel>
        <Select<ClientOption>
          {...clientOptions}
          value={client}
          onChange={setClient}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.name}
          placeholder='Search clients'
          disabled={isPending}
          dataTestId='client-select'
        />
      </FieldContainer>

      <StandardModal.Actions>
        <Button variant='ghost' onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant='primary' onClick={handleCreate} loading={isPending} disabled={!client || isPending}>
          Create List
        </Button>
      </StandardModal.Actions>
    </StandardModal>
  );
}

const Description = styled(BodySmall)`
  display: block;
  margin-bottom: 24px;
`;

// Matches the field → actions gap the denial equivalent uses, so the dropdown isn't flush against the
// Cancel / Create List buttons.
const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;
